import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, ArrowRight } from 'lucide-react';

export default function BlogSection({ blogSection }) {
  if (!blogSection.display || !blogSection.blogs.length) return null;

  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariant = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 100 },
    },
  };

  return (
    <section id="blogs" className="py-20 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 flex items-center justify-center gap-3">
            <BookOpen size={40} className="text-primary-500" />
            {blogSection.title}
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            {blogSection.subtitle}
          </p>
        </motion.div>

        {/* Blog Cards */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-8"
        >
          {blogSection.blogs.map((blog, i) => (
            <motion.a
              key={i}
              href={blog.url}
              target="_blank"
              rel="noopener noreferrer"
              variants={itemVariant}
              whileHover={{ y: -10, shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}
              className="group block"
            >
              <div className="bg-white dark:bg-slate-800 rounded-xl p-6 h-full border border-gray-200 dark:border-slate-700 hover:shadow-xl transition-shadow duration-300 flex flex-col">
                {/* Category */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full" />
                  <span className="text-sm font-semibold text-primary-600 dark:text-primary-400">
                    Tech Article
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                  {blog.title}
                </h3>

                {/* Description */}
                <p className="text-gray-600 dark:text-gray-400 mb-4 flex-grow leading-relaxed">
                  {blog.description}
                </p>

                {/* Read More */}
                <div className="flex items-center gap-2 text-primary-600 dark:text-primary-400 font-semibold group-hover:gap-4 transition-all duration-300">
                  Read Article
                  <ArrowRight size={18} />
                </div>
              </div>
            </motion.a>
          ))}
        </motion.div>

        {/* Call to action */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <p className="text-gray-600 dark:text-gray-400">
            More articles available on{' '}
            <a
              href="https://dev.to"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 dark:text-primary-400 font-semibold hover:underline"
            >
              Dev.to
            </a>
          </p>
        </motion.div>
      </div>
    </section>
  );
}
